import { useQuery } from '@apollo/client/react'
import React, { FC, useCallback, useContext } from 'react'
import { PileNode, StringPaginationType } from '@src/generated/schema'
import { Pile } from '@src/graphql/Pile'
import debounce from 'lodash.debounce'
import { MobileSearchSelect } from '@src/components/Mobile/Elements/SearchSelect'
import { NetworkContext } from '@src/components/Mobile/Providers/Network'
import { MobileSelectProps } from '@src/components/Mobile/Elements/SearchSelect/SearchSelect'
import { getUniqueList } from '@src/helpers/Logic/Logic'

export interface MobileUsageSortSelectProps extends Omit<MobileSelectProps, 'options'> {
  piles?: PileNode[]
}

export const MobileUsageSortSelect: FC<MobileUsageSortSelectProps> = ({ piles, ...props }) => {
  const { isOnline } = useContext(NetworkContext)
  const { data, loading, fetchMore, refetch } = useQuery<{ usageSorts: StringPaginationType }>(Pile.usageSorts, {
    variables: {
      pageSize: 20,
    },
  })
  const { usageSorts: { page, strings, hasNext } = { page: 0, strings: [], hasNext: false } } = data || {}

  const handleSearch = useCallback(
    debounce((filterValue: string) => {
      refetch({ filterValue, pageSize: 20 })
    }, 500),
    [],
  )

  const handlePopupScroll = () => {
    const previousPage: number = page || 0

    if (!hasNext) return

    fetchMore({
      variables: {
        page: previousPage + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const newPage = fetchMoreResult?.usageSorts?.page || 0
        const oldPage = prev?.usageSorts?.page || 0

        if (!fetchMoreResult || newPage <= oldPage) return prev

        const newEdges = fetchMoreResult.usageSorts.strings
        const previousEdges = prev?.usageSorts?.strings || []

        return newEdges?.length
          ? {
              usageSorts: {
                page: fetchMoreResult.usageSorts.page,
                pages: fetchMoreResult.usageSorts.pages,
                hasNext: fetchMoreResult.usageSorts.hasNext,
                hasPrevious: fetchMoreResult.usageSorts.hasPrevious,
                __typename: prev.usageSorts.__typename,
                strings: [...previousEdges, ...newEdges],
              },
            }
          : prev
      },
    })
  }

  return (
    <MobileSearchSelect
      {...props}
      options={
        isOnline
          ? strings?.map((usageSort) => ({
              id: usageSort,
              title: usageSort,
              value: usageSort,
            }))
          : getUniqueList(piles?.map((p) => p.usageSort)).map((usageSort) => ({
              id: usageSort,
              value: usageSort,
              title: usageSort,
            }))
      }
      loading={loading}
      multi
      onSearch={handleSearch}
      onLoadMore={handlePopupScroll}
      allowNoSelection
    />
  )
}
